import { Button } from "@mui/material";
import { useState } from "react";
import RentalFinancialProposalForm from "../forms/form";
import { useCreateProposal } from "@/modules/property-tracks/hooks/rental-financial-proposal/create-proposal.hook";
import { PreviewProposalDto } from "@/modules/property-tracks/dtos/rental-financial-proposal";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { useProposalPreview } from "@/modules/property-tracks/hooks/rental-financial-proposal/proposal-preview.hook";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import {
  ScopeOfFinishing,
  ScopeOfFinishingDisplay,
} from "@/modules/property-tracks/types";

interface CreateProposalProps {
  trackUid: string;
  defaultValues: {
    customerName: string;
    compoundName: string;
    propertyTypeName: string;
    unitArea: number;
    scopeOfFinishing?: ScopeOfFinishing;
  };
}

export default function CreateFinancialProposal({
  trackUid,
  defaultValues: {
    compoundName,
    customerName,
    propertyTypeName,
    unitArea,
    scopeOfFinishing,
  },
}: CreateProposalProps) {
  const [open, setOpen] = useState(false);

  const mutation = useCreateProposal();

  const { showPreview, PreviewModal } = useProposalPreview();

  const handleSubmit = (body: PreviewProposalDto) => {
    mutation.mutate(
      {
        data: {
          trackUid,
          ...body,
          scopeOfFinishingSnapshot: scopeOfFinishing
            ? ScopeOfFinishingDisplay[scopeOfFinishing]
            : undefined,
        },
      },
      {
        onSuccess: ({ data }) => {
          showToast({
            type: ToastType.SUCCESS,
            message: "Proposal generated successfully",
            toastId: TOAST_IDS.ADD_FINANCIAL_PROPOSAL_SUCCESS,
          });
          showPreview(data.fileUrl);
          setOpen(false);
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_FINANCIAL_PROPOSAL_ERROR,
          });
        },
      },
    );
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <Button
        disabled={readonly}
        variant="text"
        sx={(t) => ({
          textTransform: "none",
          color: t.nawy.colors.semantic.text.accent,
          ...t.nawy.typography.english.variants.body.md.semibold,
        })}
        onClick={() => setOpen(true)}
      >
        Generate
      </Button>
      <RentalFinancialProposalForm
        open={open}
        setOpen={setOpen}
        isSubmitting={mutation.isPending}
        onSubmit={handleSubmit}
        defaultValues={{
          unitDetails: {
            clientNameSnapshot: customerName,
            compoundNameSnapshot: compoundName,
            propertyTypeNameSnapshot: propertyTypeName,
            unitAreaSnapshot: unitArea,
          },
        }}
      />
      <PreviewModal />
    </>
  );
}
